import React, { useState, useEffect } from 'react';
import { UserCard } from './UserCard';
import { EditUserModal } from './EditUserModal';
import { useTeamStore } from '../../stores/teamStore';
import { User } from '../../types/user';
import { motion } from 'framer-motion';
import { ConfirmDialog } from '../common/ConfirmDialog';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

export const UserList = () => {
  const { users, roles, filters, fetchUsers, deleteUser } = useTeamStore();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({
    isOpen: false,
    user: null
  });
  const { translations } = useLanguage();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.user) return;
    
    try {
      await deleteUser(deleteConfirm.user.id);
      toast.success(translations?.team?.deleteSuccess || 'Member deleted successfully');
    } catch (error) {
      toast.error(translations?.team?.deleteError || 'Failed to delete member');
    } finally {
      setDeleteConfirm({ isOpen: false, user: null });
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteConfirm({
      isOpen: true,
      user
    });
  };

  const filteredUsers = Object.values(users).filter(user => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const matchesSearch = 
        user.name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm);
      if (!matchesSearch) return false;
    }
    if (filters.role && user.roleId !== filters.role) return false;
    if (filters.gender && user.gender !== filters.gender) return false;
    if (filters.status && user.status !== filters.status) return false;
    return true;
  });

  if (filteredUsers.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg">
        <p className="text-gray-500">
          {translations?.team?.noUsersFound || 'No users match the selected filters'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            role={roles[user.roleId]}
            onEdit={setEditingUser}
            onDelete={handleDeleteClick}
          />
        ))}
      </div>

      {editingUser && (
        <EditUserModal
          isOpen={true}
          onClose={() => setEditingUser(null)}
          user={editingUser}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, user: null })}
        onConfirm={handleDeleteConfirm}
        title={translations?.team?.deleteTitle || 'Delete Member'}
        message={`${
          translations?.team?.deleteMessage || 'Are you sure you want to delete the member'
        } "${deleteConfirm.user?.name}"? ${
          translations?.team?.deleteWarning || 'This action cannot be undone.'
        }`}
        type="danger"
        confirmText={translations?.team?.deleteConfirmText || 'Delete'}
        cancelText={translations?.team?.deleteCancelText || 'Cancel'}
      />
    </>
  );
};